import React, { useEffect, useState, useCallback, useMemo } from "react";
import geo from "../../Services/geoService";
import useForm from "../../app/hooks/useForm";
import useControls from "../../app/hooks/useControls";
import { getCurrentUser } from "../../app/store/auth";
import { useGetUserProfileLogoQuery, useUploadCompanyLogoMutation } from "../../app/store/authApiSlice";

const PROFILE_DETAILS_STYLES = {
  section: {
    borderRadius: "18px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "#ffffff",
    padding: "18px",
    marginBottom: "16px"
  },
  sectionHeader: {
    marginBottom: "14px"
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#0f172a",
    margin: 0
  },
  sectionSubtitle: {
    fontSize: "12px",
    lineHeight: 1.6,
    color: "#64748b",
    marginTop: "4px"
  },
  helperCard: {
    borderRadius: "14px",
    padding: "14px 16px",
    background: "linear-gradient(135deg, rgba(248,250,252,0.98) 0%, rgba(239,246,255,0.9) 100%)",
    border: "1px solid rgba(191, 219, 254, 0.9)",
    marginBottom: "14px"
  },
  helperTitle: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#0f172a",
    marginBottom: "6px"
  },
  helperText: {
    fontSize: "12px",
    lineHeight: 1.55,
    color: "#64748b"
  },
  verifyRow: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    marginTop: "10px"
  },
  verifyPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 14px",
    borderRadius: "8px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "#ffffff"
  },
  logoWrap: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap"
  },
  serialBadge: {
    minWidth: "30px",
    height: "30px",
    borderRadius: "999px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(79, 70, 229, 0.08)",
    color: "#4338ca",
    fontSize: "12px",
    fontWeight: 600
  },
  logoPreviewShell: {
    width: "120px",
    height: "120px",
    borderRadius: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(79, 70, 229, 0.12) 100%)",
    border: "1px solid rgba(191, 219, 254, 0.9)",
    overflow: "hidden"
  },
  logoImage: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    backgroundColor: "#ffffff"
  },
  logoInfo: {
    minWidth: 0,
    flex: 1
  },
  logoText: {
    fontSize: "12px",
    lineHeight: 1.6,
    color: "#64748b",
    marginBottom: "10px"
  },
  uploadButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "8px",
    padding: "9px 18px",
    background: "#ffffff",
    color: "#334155",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    border: "1px solid #cbd5e1",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    transition: "all 0.2s ease"
  },
  uploadButtonDisabled: {
    opacity: 0.55,
    cursor: "not-allowed"
  },
  actionRow: {
    display: "flex",
    justifyContent: "center",
    marginTop: "18px"
  },
  twoColumnRow: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "16px",
    alignItems: "stretch"
  },
  updateButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "8px",
    border: "1px solid #2563eb",
    background: "#2563eb",
    color: "#ffffff",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(37, 99, 235, 0.2)",
    cursor: "pointer",
    transition: "all 0.2s ease"
  }
};

const schema = {};

const MyProfileDetails = (props) => {
  const { userDetails, profileEdit, setProfileEdit, handleFormSubmit, usrType } = props;

  const [usStateList, setUsStateList] = useState([]);
  const [logoPicObj, setLogoPicObj] = useState(null);

  const currentUser = useMemo(() => getCurrentUser(), []);
  const usrId = currentUser?.linkUser || currentUser?.id;

  const { data: logoData = null } = useGetUserProfileLogoQuery(usrId, { skip: !usrId });
  const [uploadCompanyLogo, { isSuccess: isUploadSuccess }] = useUploadCompanyLogoMutation();

  const handleImageChange = useCallback((e) => {
    e.preventDefault();

    let reader = new FileReader();
    let file = e.target.files[0];

    if (file) {
      reader.onloadend = () => {
        setLogoPicObj({
          file: file,
          fileName: file.name,
          imagePreviewUrl: reader.result,
        });
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleUpdatelogo = useCallback(() => {
    if (!logoPicObj) return;
    const usrId = currentUser?.linkUser || currentUser?.id;
    if (!usrId) return;

    const frmData = new FormData();
    frmData.append("userId", usrId);
    frmData.append("files[0]", logoPicObj.file);
    uploadCompanyLogo(frmData);
  }, [logoPicObj, currentUser, uploadCompanyLogo]);

  const doSubmit = () => {
    handleFormSubmit(stateData);
    setProfileEdit(false);
    if (logoPicObj) {
      handleUpdatelogo();
    }
  };

  const { handleChange, stateData, setStateData, stateErrors, handleSubmit } = useForm(schema, doSubmit);

  useEffect(() => {
    const allStates = geo.getStateList();
    setUsStateList(allStates);
  }, []);

  useEffect(() => {
    if (userDetails) {
      let userName = userDetails.userName;
      let email = userDetails.performeremail;
      let name = userDetails.performername;
      let id = userDetails.performerid;
      let contactNum = userDetails.contactNo;
      let lastname = userDetails.lastName;
      let zipCode = userDetails.zipCode;

      const obj = {
        performerid: id,
        performername: name,
        performeremail: email,
        userName: userName,
        contactNo: contactNum,
        location: userDetails.location,
        state: userDetails.state,
        county: userDetails.county,
        lastName: lastname,
        zipCode: zipCode,
      };
      setStateData(obj);
    }
  }, [userDetails, setStateData]);

  useEffect(() => {
    if (isUploadSuccess) {
      setLogoPicObj(null);
    }
  }, [isUploadSuccess]);

  useEffect(() => {
    if (!profileEdit) {
      setLogoPicObj(null);
    }
  }, [profileEdit]);

  const { renderSelect, renderInput } = useControls(stateData, stateErrors, handleChange, schema);

  let $imagePreview = null;
  console.log(logoData)
  console.log(logoPicObj)
  if (logoPicObj) {
    $imagePreview = <img src={logoPicObj.imagePreviewUrl}
      alt="logo preview" style={PROFILE_DETAILS_STYLES.logoImage} />
  } else {
    $imagePreview = <img
      alt="company logo" style={PROFILE_DETAILS_STYLES.logoImage}
      src={`${!logoData ? "../../assets/img/theme/logo.png" : process.env.REACT_APP_API_BASEURL + "/CompanyLogo/" + logoData}`}
    />
  }

  return (
    <React.Fragment>
      <fieldset disabled={!profileEdit}>
        <div style={PROFILE_DETAILS_STYLES.section}>
          <div style={PROFILE_DETAILS_STYLES.sectionHeader}>
            <div style={PROFILE_DETAILS_STYLES.sectionTitle}>Basic Details</div>
            <div style={PROFILE_DETAILS_STYLES.sectionSubtitle}>
              Update the core contact information associated with your account.
            </div>
          </div>
          <div className="row">
            <div className="col-lg-6">
              {renderInput("performername", "First Name")}
            </div>
            <div className="col-lg-6">
              {renderInput("lastName", "Last Name")}
            </div>
            <div className="col-lg-6">
              {renderInput("contactNo", "Phone No.", "phone")}
            </div>
            <div className="col-lg-6">
              {renderInput("performeremail", "Email")}
            </div>
          </div>
        </div>

        <div style={PROFILE_DETAILS_STYLES.section}>
          <div style={PROFILE_DETAILS_STYLES.sectionHeader}>
            <div style={PROFILE_DETAILS_STYLES.sectionTitle}>Address Details</div>
            <div style={PROFILE_DETAILS_STYLES.sectionSubtitle}>
              Keep your mailing and location details current for account records.
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              {renderInput("location", "Address")}
            </div>

            <div className="col-md-4">
              {renderSelect(
                "state",
                "State",
                usStateList,
                "name",
                "name"
              )}
            </div>
            <div className="col-md-4">
              {renderInput("county", "City")}
            </div>
            <div className="col-md-4">
              {renderInput("zipCode", "Zip Code")}
            </div>
          </div>
        </div>

        <div style={usrType !== "candidate" ? PROFILE_DETAILS_STYLES.twoColumnRow : undefined}>
          <div style={PROFILE_DETAILS_STYLES.section}>
            <div style={PROFILE_DETAILS_STYLES.sectionHeader}>
              <div style={PROFILE_DETAILS_STYLES.sectionTitle}>Verification Preferences</div>
              <div style={PROFILE_DETAILS_STYLES.sectionSubtitle}>
                Choose how you prefer to receive verification codes when needed.
              </div>
            </div>
            <div style={PROFILE_DETAILS_STYLES.helperCard}>
              <div style={PROFILE_DETAILS_STYLES.helperTitle}>Verification methods</div>
              <div style={PROFILE_DETAILS_STYLES.helperText}>
                You'll need to confirm that the account belongs to you. Select how you would like to receive a verification code.
              </div>
              <div style={PROFILE_DETAILS_STYLES.verifyRow}>
                <label style={PROFILE_DETAILS_STYLES.verifyPill}>
                  <input id="verifyByEmail" type="checkbox" />
                  <span>Email</span>
                </label>
                <label style={PROFILE_DETAILS_STYLES.verifyPill}>
                  <input id="verifyByPhone" type="checkbox" />
                  <span>Phone No.</span>
                </label>
              </div>
            </div>
          </div>

          {
            usrType !== "candidate" ?
              (
                <div style={PROFILE_DETAILS_STYLES.section}>
                  <div style={PROFILE_DETAILS_STYLES.sectionHeader}>
                    <div style={PROFILE_DETAILS_STYLES.sectionTitle}>Company Branding</div>
                    <div style={PROFILE_DETAILS_STYLES.sectionSubtitle}>
                      Upload or replace the company logo displayed across your account.
                    </div>
                  </div>
                  <div style={PROFILE_DETAILS_STYLES.logoWrap}>
                    <div style={PROFILE_DETAILS_STYLES.logoPreviewShell}>
                      {$imagePreview}
                    </div>
                    <div style={PROFILE_DETAILS_STYLES.logoInfo}>
                      <div style={PROFILE_DETAILS_STYLES.logoText}>
                        Recommended: use a clear square or wide logo with good contrast so it stays readable across profile and account views.
                      </div>
                      <label
                        style={{
                          ...PROFILE_DETAILS_STYLES.uploadButton,
                          ...(profileEdit ? {} : PROFILE_DETAILS_STYLES.uploadButtonDisabled)
                        }}
                      >
                        <input
                          type="file"
                          onChange={handleImageChange}
                          style={{ display: "none" }}
                          disabled={!profileEdit}
                        />
                        <i className="fa fa-camera" aria-hidden="true"></i>
                        Upload Company Logo
                      </label>
                    </div>
                  </div>
                </div>
              ) : ""
          }
        </div>

        {profileEdit && (
          <div style={PROFILE_DETAILS_STYLES.actionRow}>
            <button
              type="button"
              style={PROFILE_DETAILS_STYLES.updateButton}
              onClick={handleSubmit}
            >
              <i className="fa fa-pencil" aria-hidden="true"></i>
              Update Profile
            </button>
          </div>
        )}
      </fieldset>
    </React.Fragment>
  );
}

export default MyProfileDetails;
